// RSA 암호화 유틸리티

// RSA-2048에서 OAEP 패딩을 고려한 최대 암호화 가능 크기 (바이트)
const MAX_ENCRYPT_SIZE = 190; // 안전한 크기로 설정

// Base64로 인코딩된 공개키를 CryptoKey로 변환
export async function importPublicKey(base64Key) {
  try {
    // Base64 문자열을 디코딩하여 Uint8Array로 변환
    const binaryDer = Uint8Array.from(atob(base64Key), (c) => c.charCodeAt(0));

    // 공개키를 CryptoKey로 변환
    return await window.crypto.subtle.importKey(
      'spki', // 공개키 형식 (Subject Public Key Info)
      binaryDer.buffer, // 변환된 Uint8Array의 버퍼를 전달
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256', // RSA-OAEP에 사용할 해시 알고리즘
      },
      true, // 추출 가능 여부
      ['encrypt'] // 이 키는 암호화에만 사용
    );
  } catch (error) {
    console.error('공개키 변환 실패:', error);
    throw new Error('공개키 변환에 실패했습니다.');
  }
}

// 데이터 암호화 (단일 청크)
export async function encryptData(publicKey, data) {
  try {
    const encodedData = new TextEncoder().encode(data); // 데이터를 텍스트로 인코딩
    
    // 데이터 크기 확인
    if (encodedData.length > MAX_ENCRYPT_SIZE) {
      throw new Error(`데이터가 너무 큽니다. 최대 ${MAX_ENCRYPT_SIZE}바이트까지 암호화 가능합니다.`);
    }
    
    const encryptedData = await window.crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
      },
      publicKey, // 공개키로 암호화
      encodedData
    );

    return encryptedData; // 암호화된 데이터 반환
  } catch (error) {
    console.error('데이터 암호화 실패:', error);
    throw new Error('데이터 암호화에 실패했습니다.');
  }
}

// 큰 데이터를 청크 단위로 암호화
export async function encryptDataChunks(publicKey, data) {
  try {
    const encodedData = new TextEncoder().encode(data);
    const chunks = [];
    const encryptedChunks = [];

    // 데이터를 청크로 분할
    for (let i = 0; i < encodedData.length; i += MAX_ENCRYPT_SIZE) {
      const chunk = encodedData.slice(i, i + MAX_ENCRYPT_SIZE);
      chunks.push(chunk);
    }

    // 각 청크를 암호화
    for (const chunk of chunks) {
      const encryptedChunk = await window.crypto.subtle.encrypt(
        { name: 'RSA-OAEP' },
        publicKey,
        chunk
      );
      encryptedChunks.push(new Uint8Array(encryptedChunk));
    }

    return encryptedChunks;
  } catch (error) {
    console.error('청크 암호화 실패:', error);
    throw new Error('데이터 암호화에 실패했습니다.');
  }
}

// ArrayBuffer를 Base64로 변환
export const bufferToBase64 = (buffer) => {
  const binary = String.fromCharCode.apply(null, new Uint8Array(buffer));
  return btoa(binary);
};

// 청크 배열을 Base64로 변환
export const chunksToBase64 = (chunks) => {
  return chunks.map(chunk => bufferToBase64(chunk));
};

// 스마트 암호화 - 크기에 따라 단일/청크 암호화 선택
export async function smartEncrypt(publicKey, data) {
  const encodedData = new TextEncoder().encode(data);
  
  if (encodedData.length <= MAX_ENCRYPT_SIZE) {
    // 작은 데이터는 단일 암호화
    const encrypted = await encryptData(publicKey, data);
    return {
      type: 'single',
      data: bufferToBase64(encrypted)
    };
  } else {
    // 큰 데이터는 청크 암호화
    const encryptedChunks = await encryptDataChunks(publicKey, data);
    return {
      type: 'chunks',
      data: chunksToBase64(encryptedChunks)
    };
  }
}

// PIN 번호 암호화 (TV 인증용) - 항상 작은 크기이므로 단일 암호화
export async function encryptPinCode(pinCode, publicKey) {
  try {
    const publicKeyObj = await importPublicKey(publicKey);
    const encryptedPin = await encryptData(publicKeyObj, pinCode);
    return bufferToBase64(encryptedPin);
  } catch (error) {
    console.error('PIN 코드 암호화 실패:', error);
    throw new Error('PIN 코드 암호화에 실패했습니다.');
  }
}

// 로그인 데이터 암호화 (개선된 버전)
export async function encryptLoginData(username, password, publicKey) {
  try {
    const publicKeyObj = await importPublicKey(publicKey);
    
    // 사용자명과 비밀번호를 스마트 암호화
    const encryptedUsername = await smartEncrypt(publicKeyObj, username);
    const encryptedPassword = await smartEncrypt(publicKeyObj, password);
    
    return {
      username: encryptedUsername,
      password: encryptedPassword
    };
  } catch (error) {
    console.error('로그인 데이터 암호화 실패:', error);
    throw new Error('로그인 데이터 암호화에 실패했습니다.');
  }
}

// 간단한 문자열 암호화 (기존 호환성 유지)
export async function encryptString(publicKey, data) {
  try {
    const publicKeyObj = await importPublicKey(publicKey);
    const encrypted = await encryptData(publicKeyObj, data);
    return bufferToBase64(encrypted);
  } catch (error) {
    console.error('문자열 암호화 실패:', error);
    throw new Error('문자열 암호화에 실패했습니다.');
  }
}